import axios from "axios";

const BASE_URL = process.env.REACT_APP_BASE_API_URL;

// List matches with optional filters + pagination
// Expected response: { items, page, pageSize, totalItems, totalPages }
// Supported params: needId, consultantId, positioned, submissionStatus, page, pageSize, etc.
export const listMatches = async (params = {}, options = {}) => {
  const { buId } = options || {};
  const response = await axios.get(`${BASE_URL}/matches`, {
    params,
    ...(buId ? { headers: { "X-Business-Unit-Id": buId } } : {})
  });
  return response.data;
};

// List matches for a consultant, filtered to those submitted to client
// GET /matches?consultantId={id}&submissionStatus=submitted_to_client
// Backend filters by consultant and submission status; more efficient than fetching all needs.
export const listMatchesForConsultant = async (consultantId, options = {}) => {
  const { buId, page = 1, pageSize = 500 } = options;
  return listMatches(
    {
      consultantId,
      submissionStatus: "submitted_to_client",
      page,
      pageSize
    },
    { buId }
  );
};

// Get a single match
// GET /matches/{match_id}
export const getMatch = async (matchId, options = {}) => {
  const { buId } = options || {};
  const response = await axios.get(`${BASE_URL}/matches/${matchId}`, {
    ...(buId ? { headers: { "X-Business-Unit-Id": buId } } : {})
  });
  return response.data;
};

// Generate and persist matches for a given need
// POST /matches/generate-for-need/{need_id}
// Optional body: { consultantIds?: number[] }
// Returns list[MatchItem]
export const generateMatchesForNeed = async (needId, payload = null, options = {}) => {
  const { buId } = options || {};
  const response = await axios.post(
    `${BASE_URL}/matches/generate-for-need/${needId}`,
    payload,
    {
      ...(buId ? { headers: { "X-Business-Unit-Id": buId } } : {})
    }
  );
  return response.data;
};

// Generate matches for a consultant across all open needs
// POST /matches/generate-for-consultant/{consultant_id}
// Returns list[MatchItem] (all matches for this consultant)
export const generateMatchesForConsultant = async (consultantId, options = {}) => {
  const { buId } = options || {};
  const response = await axios.post(
    `${BASE_URL}/matches/generate-for-consultant/${consultantId}`,
    null,
    {
      ...(buId ? { headers: { "X-Business-Unit-Id": buId } } : {})
    }
  );
  return response.data;
};

// Delete a single match
export const deleteMatch = async (matchId, options = {}) => {
  const { buId } = options || {};
  const response = await axios.delete(`${BASE_URL}/matches/${matchId}`, {
    ...(buId ? { headers: { "X-Business-Unit-Id": buId } } : {})
  });
  return response.data;
};

// Create a match manually
// POST /matches
// Supports mission/PO/satisfaction fields:
// - assignmentStatus
// - assignmentStartDate (YYYY-MM-DD)
// - assignmentEndDate (YYYY-MM-DD)
// - purchaseOrderStatus
// - purchaseOrderNumber
// - purchaseOrderReceivedAt (ISO datetime)
// - purchaseOrderEndDate (YYYY-MM-DD)
// - clientSatisfactionScore
// - clientSatisfactionComment
export const createMatch = async (payload, options = {}) => {
  const { buId } = options || {};
  const response = await axios.post(`${BASE_URL}/matches`, payload, {
    ...(buId ? { headers: { "X-Business-Unit-Id": buId } } : {})
  });
  return response.data;
};

// Patch / update an existing match
// PATCH /matches/{match_id}
// Accepts the same mission/PO/satisfaction fields as createMatch
export const updateMatch = async (matchId, payload, options = {}) => {
  const { buId } = options || {};
  const response = await axios.patch(`${BASE_URL}/matches/${matchId}`, payload, {
    ...(buId ? { headers: { "X-Business-Unit-Id": buId } } : {})
  });
  return response.data;
};

// Manual submission workflow transitions
// POST /matches/{match_id}/submit-to-client
export const submitMatchToClient = async (matchId, payload = {}, options = {}) => {
  const { buId } = options || {};
  const response = await axios.post(
    `${BASE_URL}/matches/${matchId}/submit-to-client`,
    payload,
    {
      ...(buId ? { headers: { "X-Business-Unit-Id": buId } } : {})
    }
  );
  return response.data;
};

// POST /matches/{match_id}/withdraw-submission
export const withdrawMatchSubmission = async (matchId, options = {}) => {
  const { buId } = options || {};
  const response = await axios.post(
    `${BASE_URL}/matches/${matchId}/withdraw-submission`,
    null,
    {
      ...(buId ? { headers: { "X-Business-Unit-Id": buId } } : {})
    }
  );
  return response.data;
};

// POST /matches/{match_id}/mark-client-rejected
export const markMatchClientRejected = async (matchId, options = {}) => {
  const { buId } = options || {};
  const response = await axios.post(
    `${BASE_URL}/matches/${matchId}/mark-client-rejected`,
    null,
    {
      ...(buId ? { headers: { "X-Business-Unit-Id": buId } } : {})
    }
  );
  return response.data;
};

// Workflow pipeline transition
// POST /matches/{match_id}/workflow-transition
// Body: { targetStatus: string, force?: boolean }
export const transitionMatchWorkflow = async (matchId, targetStatus, force = false, options = {}) => {
  const { buId } = options || {};
  const response = await axios.post(
    `${BASE_URL}/matches/${matchId}/workflow-transition`,
    { targetStatus, force },
    {
      ...(buId ? { headers: { "X-Business-Unit-Id": buId } } : {})
    }
  );
  return response.data;
};

// List interviews for a match
// GET /matches/{match_id}/interviews
export const listInterviewsForMatch = async (matchId, options = {}) => {
  const { buId } = options || {};
  const response = await axios.get(`${BASE_URL}/matches/${matchId}/interviews`, {
    ...(buId ? { headers: { "X-Business-Unit-Id": buId } } : {})
  });
  return response.data;
};

// Get a single interview
// GET /matches/interviews/{interview_id}
export const getInterview = async (interviewId, options = {}) => {
  const { buId } = options || {};
  const response = await axios.get(`${BASE_URL}/matches/interviews/${interviewId}`, {
    ...(buId ? { headers: { "X-Business-Unit-Id": buId } } : {})
  });
  return response.data;
};

// Create a new interview for a match
// POST /matches/{match_id}/interviews
export const createInterviewForMatch = async (matchId, payload, options = {}) => {
  const { buId } = options || {};
  const response = await axios.post(`${BASE_URL}/matches/${matchId}/interviews`, payload, {
    ...(buId ? { headers: { "X-Business-Unit-Id": buId } } : {})
  });
  return response.data;
};

// Update an interview
// PATCH /matches/interviews/{interview_id}
export const updateInterview = async (interviewId, payload, options = {}) => {
  const { buId } = options || {};
  const response = await axios.patch(`${BASE_URL}/matches/interviews/${interviewId}`, payload, {
    ...(buId ? { headers: { "X-Business-Unit-Id": buId } } : {})
  });
  return response.data;
};

// List matches grouped by consultant
// GET /matches/grouped-by-consultant
export const listMatchesGroupedByConsultant = async (params = {}, options = {}) => {
  const { buId } = options || {};
  const response = await axios.get(`${BASE_URL}/matches/grouped-by-consultant`, {
    params,
    ...(buId ? { headers: { "X-Business-Unit-Id": buId } } : {})
  });
  return response.data;
};

// Reverse match: find best open needs for a consultant
// POST /matches/reverse-match/{consultant_id}
export const reverseMatchForConsultant = async (consultantId, options = {}) => {
  const { buId } = options || {};
  const response = await axios.post(
    `${BASE_URL}/matches/reverse-match/${consultantId}`,
    null,
    {
      ...(buId ? { headers: { "X-Business-Unit-Id": buId } } : {})
    }
  );
  return response.data;
};

